// src/components/practice/tasks/OpinionMatching.jsx
import React from 'react';
// We no longer import renderExample
import { FlagButton } from '../PartDisplay';

/**
 * REDESIGNED: Paper Exam Style (Aligned Blocks)
 *
 * This layout creates a new two-column grid for EACH opinion/question pair.
 * This ensures that the start of an opinion (e.g., "Person A") is always
 * horizontally aligned with the start of its associated Ja/Nein question.
 *
 * MODIFIED: Examples are now integrated into this mapping.
 */
const OpinionMatching = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  ...props
}) => {
  const allProps = { flaggedQuestions, toggleFlagQuestion, ...props };
  const opinionTexts = partData.content?.texts || [];

  // This is the common question text (e.g., "Ist die Person für ein Verbot?")
  const mainQuestionText = partData.questionText || "Stimmt die Person zu?";

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">

      {/* --- Examples (Global, at the top) REMOVED --- */}

      {/* Main Question Text Header (Unchanged) */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-6">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antworten
        </h3>
        <p className="text-sm text-gray-800 font-semibold mt-2">
          {mainQuestionText}
        </p>
      </div>

      {/* Main content area: Map over examples AND questions */}
      <div className="space-y-6">

        {/* --- NEW: Map over examples first --- */}
        {examples.map(ex => {
          const opinionText = opinionTexts.find(t => t.id === ex.personId);
          if (!opinionText) return null;

          const correctAnswer = ex.correctAnswer; // "Ja" or "Nein"

          return (
            // This grid creates the Opinion A | Question A alignment
            <div key={ex.questionId} className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">

              {/* --- LEFT COLUMN (Example Opinion Text) --- */}
              <div className="lg:col-span-3">
                <div className="border-2 border-gray-900 bg-yellow-50 p-4 rounded-none h-full opacity-90">
                  <p className="font-bold text-gray-900 mb-2">{opinionText.author}</p>
                  <p className="text-gray-800 italic leading-relaxed text-sm">
                    "{opinionText.content}"
                  </p>
                </div>
              </div>

              {/* --- RIGHT COLUMN (Example Answer Box) --- */}
              <div className="lg:col-span-2 mt-4 lg:mt-0">
                <div
                  className="border border-gray-900 bg-yellow-50 p-4 rounded-none h-full opacity-90"
                >
                  {/* Example Question Text (no flag) */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <span className="font-bold text-sm mr-2">
                        {ex.questionNumber}.
                      </span>
                      <span className="text-sm leading-relaxed font-semibold">
                        {opinionText.author}
                      </span>
                    </div>
                    {/* No FlagButton for examples */}
                  </div>

                  {/* Ja/Nein Options - Disabled */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Ja Button */}
                    <button
                      disabled
                      className={`
                        border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center cursor-not-allowed
                        ${correctAnswer === 'Ja'
                          ? 'border-gray-900 bg-gray-900 text-white' // Selected
                          : 'border-gray-400 bg-white text-gray-900' // Default
                        }
                      `}
                    >
                      Ja
                    </button>
                    {/* Nein Button */}
                    <button
                      disabled
                      className={`
                        border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center cursor-not-allowed
                        ${correctAnswer === 'Nein'
                          ? 'border-gray-900 bg-gray-900 text-white' // Selected
                          : 'border-gray-400 bg-white text-gray-900' // Default
                        }
                      `}
                    >
                      Nein
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* --- Map over regular questions (Unchanged) --- */}
        {questions.map(q => {
          const opinionText = opinionTexts.find(t => t.id === q.personId);
          if (!opinionText) return null;

          const currentAnswer = partAnswers[q.questionId];

          return (
            // This grid creates the Opinion A | Question A alignment
            <div key={q.questionId} className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">

              {/* --- LEFT COLUMN (Opinion Text) --- */}
              <div className="lg:col-span-3">
                <div className="border-2 border-gray-900 bg-white p-4 rounded-none h-full">
                  <p className="font-bold text-gray-900 mb-2">{opinionText.author}</p>
                  <p className="text-gray-800 italic leading-relaxed text-sm">
                    "{opinionText.content}"
                  </p>
                </div>
              </div>

              {/* --- RIGHT COLUMN (Answer Box) --- */}
              <div className="lg:col-span-2 mt-4 lg:mt-0">
                <div
                  className="border border-gray-900 bg-white p-4 rounded-none h-full"
                >
                  {/* Question Text with Flag */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <span className="font-bold text-sm mr-2">
                        {q.questionNumber}.
                      </span>
                      <span className="text-sm leading-relaxed font-semibold">
                        {opinionText.author}
                      </span>
                    </div>
                    <FlagButton {...allProps} questionId={q.questionId} />
                  </div>

                  {/* Ja/Nein Options - Paper Style Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Ja Button */}
                    <button
                      onClick={() => handleSingleAnswer(q.questionId, 'Ja')}
                      className={`
                        border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center
                        ${currentAnswer === 'Ja'
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-400 bg-white text-gray-900 hover:border-gray-900'
                        }
                      `}
                    >
                      Ja
                    </button>
                    {/* Nein Button */}
                    <button
                      onClick={() => handleSingleAnswer(q.questionId, 'Nein')}
                      className={`
                        border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center
                        ${currentAnswer === 'Nein'
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-400 bg-white text-gray-900 hover:border-gray-900'
                        }
                      `}
                    >
                      Nein
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OpinionMatching;